import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Grid, Textarea } from "@mantine/core";
import { getPaymentMethodLabelByValue } from "../../utils/paymentMethodsList.js";
import { formatCurrency } from "../../utils/printUtils.js";
import { calculateCreditSummary } from "../../utils/creditCalculations";
import "./TicketContent.scss";
import { getPage } from "../../services/page.js";

const TicketContent = ({
  tickedCustomMessage,
  ticketFontStyle,
  internalName,
  paymentMethod,
  details,
  editDetails,
  setEditDetails,
  printing,
  currentProducts,
  total,
  payment,
  creditDetails,
  orderId,
  ticketDate,
  readOnly,
  pendingOrderData,
}) => {
  const [page, setPage] = useState(null);
  const userData = useSelector((state) => state.user.user);
  useEffect(() => {
    const fetchPage = async () => {
      const possibleUserId = currentProducts?.[0]?.userId || userData?._id;
      const hasValidUserId = Boolean(possibleUserId);
      if (!hasValidUserId) return;
      const pageData = await getPage(possibleUserId);
      setPage(pageData.data);
    };
    fetchPage();
  }, [currentProducts, userData?._id]);
  return (
    <div
      className={`print-ticket ${printing ? "printing" : "reveal"}`}
      style={{
        fontFamily: ticketFontStyle?.fontFamily || "Arial",
        fontSize: `${ticketFontStyle?.fontSize || 14}px`,
        fontWeight: ticketFontStyle?.fontWeight || "normal",
      }}
    >
      <Grid style={{ textAlign: "left", margin: 0 }}>
        {page?.logo ? (
          <>
            {page?.logo && (
              <Grid.Col
                span={4}
                style={{ padding: 0 }}
              >
                <img
                  src={page.logo}
                  alt="logo"
                  width="100px"
                  style={{ maxWidth: "100%" }}
                />
              </Grid.Col>
            )}
            <Grid.Col
              span="auto"
              style={{ padding: 0 }}
            >
              <b>{page?.title}</b>
              <br />
              Ticket de compra
              <br />
              <small>{ticketDate}</small>
            </Grid.Col>
          </>
        ) : (
          <>
            <b>{page?.title}</b>
            <br />
            Ticket de compra
            <br />
            <small>{ticketDate}</small>
            <br />
            <small>Ticket #: {orderId}</small>
          </>
        )}
      </Grid>
      <br />

      {tickedCustomMessage && (
        <p className="custom-content">
          {tickedCustomMessage.split("\n").map((line, index) => (
            <Fragment key={index}>
              {line}
              <br />
            </Fragment>
          ))}
          <br />
        </p>
      )}

      {internalName && (
        <>
          <div className="breaker"></div>
          <div style={{ textAlign: "left" }}>
            Cliente: <b>{internalName}</b>
            <br />
          </div>
        </>
      )}

      {paymentMethod && (
        <>
          <div className="breaker"></div>
          <div style={{ textAlign: "left" }}>
            Método de pago: <b>{getPaymentMethodLabelByValue(paymentMethod)}</b>
            <br />
          </div>
        </>
      )}

      {details ? (
        <div style={{ textAlign: "left" }}>
          {printing || readOnly ? (
            <pre>{editDetails}</pre>
          ) : (
            <Textarea
              style={{
                border: "solid 3px #3399ff",
                borderRadius: "5px",
                fontFamily: ticketFontStyle?.fontFamily || "Arial",
                fontSize: `${ticketFontStyle?.fontSize || 14}px`,
                fontWeight: ticketFontStyle?.fontWeight || "normal",
              }}
              placeholder="Escribe tu mensaje aquí"
              value={editDetails}
              onChange={(e) => setEditDetails(e.currentTarget.value)}
              rows={4}
              autosize
            />
          )}
          <br />
        </div>
      ) : (
        <>
          <div className="breaker"></div>
          <div className="print-ticket__content">
            <table
              className="ticket-table"
              style={{ boxShadow: "none", width: "100%" }}
            >
              <thead>
                <tr className="ticket-item">
                  <th style={{ background: "transparent", color: "#333" }}>
                    Producto
                  </th>
                  <th style={{ background: "transparent", color: "#333" }}>
                    P/U
                  </th>
                  <th style={{ background: "transparent", color: "#333" }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentProducts.map((item) => {
                  const productQuantity = Number(item.quantity || 0);
                  const itemTotal =
                    Number(item.price || 0) * productQuantity;
                  const discountAmount = Number(item.discount || 0);
                  const discountedTotal = itemTotal - discountAmount;
                  return (
                    <tr
                      key={item._id}
                      className="ticket-item"
                    >
                      <td>
                        {item.name} x{productQuantity}
                        {item.discount && (
                          <>
                            <br />
                            <b>
                              Descuento: $ {formatCurrency(itemTotal)} - ${" "}
                              {formatCurrency(item.discount)} = ${" "}
                              {formatCurrency(discountedTotal)}
                            </b>
                          </>
                        )}
                      </td>
                      <td>${formatCurrency(item.price)}</td>
                      <td>
                        {item.discount
                          ? formatCurrency(discountedTotal)
                          : formatCurrency(itemTotal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="breaker"></div>

      <div className="ticket-item">
        <span>
          <h3>TOTAL</h3>
        </span>
        <span>
          <h3>${formatCurrency(total)}</h3>
        </span>
      </div>

      {creditDetails ? (
        <div className="ticket-item">
          <span>
            <h3>Pago Inicial</h3>
          </span>
          <span>
            <h3>${formatCurrency(creditDetails.initialPayment)}</h3>
          </span>
        </div>
      ) : (
        <>
          {payment > 0 && (
            <>
              <div className="ticket-item">
                <span>
                  <h3>Pago</h3>
                </span>
                <span>
                  <h3>${formatCurrency(payment)}</h3>
                </span>
              </div>
              <div className="ticket-item">
                <span>
                  <h3>Cambio</h3>
                </span>
                <span>
                  <h3>${formatCurrency(payment - total)}</h3>
                </span>
              </div>
            </>
          )}
        </>
      )}

      {creditDetails &&
        (() => {
          const summary = calculateCreditSummary({
            subtotal: total,
            initialPayment: creditDetails.initialPayment,
            interestRate: creditDetails.interestRate,
            numberOfPayments: creditDetails.numberOfPayments,
            latePaymentInterestRate: creditDetails.latePaymentInterestRate,
            toleranceDays: creditDetails.toleranceDays,
          });
          return (
            <>
              <div className="breaker"></div>
              <div className="ticket-item">
                <div>
                  <h3>Venta a Crédito</h3>
                </div>
              </div>
              <div style={{ textAlign: "left", marginTop: "10px" }}>
                <div className="ticket-item">
                  <span>Pago Inicial:</span>
                  <span>${summary.initialPayment.toFixed(2)}</span>
                </div>
                <div className="ticket-item">
                  <span>Monto a Crédito:</span>
                  <span>${summary.amountToCredit.toFixed(2)}</span>
                </div>
                <div className="ticket-item">
                  <span>Interés Base:</span>
                  <span>
                    ${summary.interest.toFixed(2)} ({summary.interestRate}%)
                  </span>
                </div>
                <div className="ticket-item">
                  <span>Interés por Atraso:</span>
                  <span>
                    ${summary.lateInterest.toFixed(2)} (
                    {summary.latePaymentInterestRate}%)
                  </span>
                </div>
                <div className="ticket-item">
                  <span>Número de Pagos:</span>
                  <span>{summary.numberOfPayments}</span>
                </div>
                <div className="ticket-item">
                  <span>Monto por Pago:</span>
                  <span>${summary.paymentAmount.toFixed(2)}</span>
                </div>
                <div className="ticket-item">
                  <span>Monto por Pago Atrasado:</span>
                  <span>${summary.latePaymentAmount.toFixed(2)}</span>
                </div>
                <div className="ticket-item">
                  <span>Total con Interés:</span>
                  <span>${summary.totalWithInterest.toFixed(2)}</span>
                </div>
                <div className="ticket-item">
                  <span>Días de Tolerancia:</span>
                  <span>{summary.toleranceDays} días</span>
                </div>
              </div>
            </>
          );
        })()}

      {pendingOrderData &&
        pendingOrderData.missingProducts &&
        pendingOrderData.missingProducts.length > 0 && (
          <>
            <div className="breaker"></div>
            <div style={{ textAlign: "left", marginTop: "10px" }}>
              <h3 style={{ color: "#ff6b6b" }}>
                Productos Pendientes de Entrega
              </h3>
              {pendingOrderData.missingProducts.map((product, index) => (
                <div
                  key={index}
                  className="ticket-item"
                  style={{ marginTop: "5px" }}
                >
                  <span>{product.name}</span>
                  <span>Faltan: {product.missingQuantity}</span>
                </div>
              ))}
              <div
                style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}
              >
                * Presente este ticket al recoger sus productos pendientes
              </div>
            </div>
          </>
        )}

      <br />
    </div>
  );
};

export default TicketContent;
